import React, { useState } from "react";
import { useAudioEditor } from "../../../../context/AudioEditorContext";
import { useTranslation } from "react-i18next";
import IconButton from "../../IconButton";
import { Tooltip } from "@mui/material";
import Icon from "../../Icon";
import { AudioEditorClipRole } from "../../../../types/types";
import styles from "./AudioEditorTrackMixer.module.css";

interface SidebarIcon {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    onClick?: (e?: Event) => void;
    id?: string;
    isSelected?: boolean;
    disabled?: string;
}

interface AudioEditorTrackSidebarItemProps {
    trackLabel: string;
    noteIcon: string;
    icons: SidebarIcon[];
    trackIndex: number;
    track: any;
    iconSize?: number;
    /** Voice / music / sound effect. Undefined on tracks with no known provenance. */
    role?: AudioEditorClipRole;
}

const ROLE_STYLES: Record<AudioEditorClipRole, { className: string; icon: string; labelKey: string }> = {
    voice: { className: styles.roleVoice, icon: "ic-mic", labelKey: "trackRoleVoice" },
    music: { className: styles.roleMusic, icon: "ic-music", labelKey: "trackRoleMusic" },
    sfx: { className: styles.roleSfx, icon: "ic-volume", labelKey: "trackRoleSfx" },
    sound: { className: styles.roleSound, icon: "ic-audio-lines", labelKey: "trackRoleSound" },
};

const AudioEditorTrackSidebarItem: React.FC<AudioEditorTrackSidebarItemProps> = ({
    trackLabel,
    noteIcon,
    icons,
    trackIndex,
    track,
    iconSize = 16,
    role,
}) => {
    const { selectedTrack, setSelectedTrack, setActiveTrack } = useAudioEditor();

    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedIcons, setSelectedIcons] = useState<{ [key: string]: boolean }>({});

    const isSelected = selectedTrack?.index === trackIndex;
    const roleStyle = role ? ROLE_STYLES[role] : undefined;

    const handleSelect = () => {
        if (isSelected) {
            setSelectedTrack(null, null);
        } else {
            setSelectedTrack(track, trackIndex);
            setActiveTrack?.(trackIndex);
        }
    };

    const handleIconClick = (e: React.MouseEvent, iconId: string, onClick?: () => void, isControlled?: boolean) => {
        e.stopPropagation();
        onClick?.();
        if (!isControlled) {
            setSelectedIcons((prev) => ({
                ...prev,
                [iconId]: !prev[iconId],
            }));
        }
    };

    return (
        <div
            className={[
                styles.trackSidebarItem,
                roleStyle?.className,
                isSelected ? styles.trackSidebarItemSelected : "",
            ]
                .filter(Boolean)
                .join(" ")}
            onClick={handleSelect}
        >
            <div className={styles.trackHeader}>
                {roleStyle ? (
                    <Tooltip title={t(roleStyle.labelKey)} placement="top">
                        <span className={styles.trackRoleIconWrap}>
                            <Icon name={roleStyle.icon} className={styles.trackRoleIcon} />
                        </span>
                    </Tooltip>
                ) : (
                    <img
                        src={noteIcon}
                        alt={t("track")}
                        className={styles.trackNote}
                        width={iconSize}
                        height={iconSize}
                    />
                )}
                <Tooltip title={trackLabel} placement="top">
                    <span className={styles.trackLabel}>{trackLabel}</span>
                </Tooltip>
            </div>

            <div className={styles.trackActions}>
                {icons.map(({ icon, title, onClick, id, isSelected, disabled }, i) => {
                    const iconId = `${trackLabel}-icon-${id}-${i}`;
                    const isIconSelected = isSelected !== undefined ? isSelected : !!selectedIcons[iconId];

                    return (
                        <IconButton
                            key={iconId}
                            IconComponent={icon}
                            title={disabled || title}
                            iconSize={iconSize}
                            isSelected={isIconSelected}
                            onClick={(e) => handleIconClick(e, iconId, onClick, isSelected !== undefined)}
                            disabled={!!disabled}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default AudioEditorTrackSidebarItem;
